/**
 * The dialog behind the workspace switch's "Add workspace" row.
 *
 * Ours rather than the fleet's: the fleet's own modal is written against
 * tiketo's mutations, which `WorkspaceSwitch.tsx`'s own header already
 * explains this console does not take. Built the way `APITokensSettings`'
 * create dialog is instead — a plain `Dialog`, a name field, and the API's
 * own refusal shown inline rather than reworded (`useCreateWorkspace`'s
 * comment gives the reason a second copy of "you may own at most 5" would be
 * a second thing to keep in step).
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ApiError } from "../../lib/api";
import { useCreateWorkspace } from "../../lib/session";
import type { Membership } from "../../lib/session";

export interface CreateWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called once the workspace exists, with what the API handed back. */
  onCreated: (workspace: Membership) => void;
}

export function CreateWorkspaceModal({ open, onOpenChange, onCreated }: CreateWorkspaceModalProps) {
  const [name, setName] = useState("");
  const createWorkspace = useCreateWorkspace();

  /*
   * Cleared on open only — the same reason and the same shape as
   * `APITokensSettings`' `openCreateDialog`. A refusal from the workspace
   * created two tries ago must not still be on screen when this one opens,
   * and the request itself is not cancelled by closing this dialog while it
   * is in flight, so there is nothing on the closing edge to also clear.
   */
  const handleOpenChange = (next: boolean) => {
    if (next) {
      createWorkspace.reset();
      setName("");
    }
    onOpenChange(next);
  };

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    createWorkspace.mutate(
      { name: trimmed },
      {
        onSuccess: (workspace) => {
          // Before `onCreated`, which may navigate away and unmount this
          // dialog: the field must not still hold the name the next "Add
          // workspace" opens on.
          setName("");
          onOpenChange(false);
          onCreated(workspace);
        },
      },
    );
  };

  const errorMessage =
    createWorkspace.error instanceof ApiError
      ? createWorkspace.error.message
      : createWorkspace.error
        ? "Failed to create workspace"
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add workspace</DialogTitle>
          <DialogDescription>
            A workspace of your own — separate projects, separate API tokens. Nothing in it is
            shared with anyone else.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="workspace-name" className="text-right">
              Name
            </Label>
            <Input
              id="workspace-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="col-span-3"
              placeholder="e.g., Client project"
              autoFocus
            />
          </div>
          {errorMessage ? (
            <p role="alert" className="text-sm text-destructive">
              {errorMessage}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="submit" onClick={handleCreate} disabled={createWorkspace.isPending || !name.trim()}>
            {createWorkspace.isPending ? "Creating..." : "Create workspace"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
